import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import {
  CHANNEL_LABELS,
  COMMITTEE_ROLE_LABELS,
  isReachable,
  METER_STATUS_LABELS,
  matchesWords,
  PHONE_STATUS_LABELS,
  present,
} from '@horof/shared';
import { Alert, Button, Input, Segmented, Table, Tag } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from 'urql';
import { MemberPanel } from './MemberPanel';
import { useSplit } from './panel';
import { type MemberRow, MembersDocument, peopleContext } from './people-data';
import { useFitColumns } from './table-fit';
import { ListHeader, TableCard } from './ui';
import { useIsCommittee } from './water-data';

type View = 'active' | 'unreachable' | 'unnamed' | 'committee' | 'left';

/**
 * «الأعضاء»: every member with their meters and how messages reach them;
 * views for the work left (no WhatsApp, unnamed shart people), the
 * committee, and those who left. A row opens its panel.
 */
export function MembersScreen() {
  const { ref: fitRef, fit } = useFitColumns();
  const canEdit = useIsCommittee();
  const split = useSplit();
  const navigate = useNavigate();
  // A row opens the record's page (doc 58); old `?panel=<id>` links too.
  // The panel stays for a new record.
  useEffect(() => {
    if (split.panel && split.panel !== 'new') {
      navigate(`/members/${split.panel}`, { replace: true });
    }
  }, [split.panel, navigate]);
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>('active');
  const [{ data, error, fetching }] = useQuery({
    query: MembersDocument,
    context: useMemo(() => peopleContext, []),
  });
  const members = present(data?.members?.nodes);
  const active = members.filter((m) => m.status === 'ACTIVE');
  const views: Record<View, MemberRow[]> = {
    active,
    unreachable: active.filter((m) => !isReachable(m)),
    unnamed: members.filter((m) => m.unnamedShart.totalCount > 0),
    committee: members.filter((m) => m.committeeRole),
    left: members.filter((m) => m.status === 'LEFT'),
  };
  const rows = views[view].filter((m) =>
    matchesWords(
      [
        m.fullName,
        m.memberAddress,
        m.notifyPhone,
        m.email,
        ...present(m.meters.nodes).map((x) => x.label),
      ],
      search,
    ),
  );

  return (
    <div className="desk-split">
      <section className="desk-page" ref={fitRef}>
        <ListHeader
          title="الأعضاء"
          count={data ? views.active.length : undefined}
          description="المنخرطون في الجمعية. يُفتح العضو في صفحته."
          actions={
            canEdit && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => split.open('new')}
              >
                عضو جديد
              </Button>
            )
          }
        />
        <TableCard
          toolbar={
            <>
              <Segmented<View>
                value={view}
                onChange={setView}
                options={[
                  {
                    value: 'active',
                    label: `المنخرطون (${views.active.length})`,
                  },
                  {
                    value: 'unreachable',
                    label: `بدون واتساب (${views.unreachable.length})`,
                  },
                  {
                    value: 'unnamed',
                    label: `شرط بلا اسم (${views.unnamed.length})`,
                  },
                  {
                    value: 'committee',
                    label: `اللجنة (${views.committee.length})`,
                  },
                  { value: 'left', label: `غادروا (${views.left.length})` },
                ]}
              />
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="بحث: الاسم، العداد، الهاتف، العنوان"
                className="desk-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </>
          }
          bars={
            (view === 'committee' || error) && (
              <>
                {view === 'committee' && (
                  <div className="field-hint">
                    من له بريد إلكتروني يتوصل بالتقرير الأسبوعي كل يوم اثنين.
                  </div>
                )}
                {error && (
                  <Alert type="error" showIcon message="تعذّر تحميل الأعضاء" />
                )}
              </>
            )
          }
        >
          <Table<MemberRow>
            rowKey="id"
            size="small"
            tableLayout="fixed"
            dataSource={rows}
            loading={fetching && !data}
            pagination={false}
            scroll={{ y: 'calc(100vh - 240px)' }}
            rowClassName={(m) =>
              m.id === split.panel ? 'is-open is-clickable' : 'is-clickable'
            }
            onRow={(m) => ({ onClick: () => navigate(`/members/${m.id}`) })}
            locale={{ emptyText: search ? 'لا عضو بهذا البحث' : 'لا أحد' }}
            columns={fit<MemberRow>([
              {
                key: 'name',
                title: 'العضو',
                ellipsis: true,
                render: (_, m) => (
                  <span>
                    <strong>{m.fullName}</strong>
                    {m.committeeRole && (
                      <Tag color="purple" className="desk-inline-tag">
                        {COMMITTEE_ROLE_LABELS[m.committeeRole]}
                      </Tag>
                    )}
                    {m.status === 'LEFT' && (
                      <Tag className="desk-inline-tag">غادر</Tag>
                    )}
                  </span>
                ),
              },
              ...[
                {
                  key: 'meters',
                  drop: 3,
                  title: 'العدادات',
                  ellipsis: true,
                  render: (_: unknown, m: MemberRow) =>
                    present(m.meters.nodes)
                      .map(
                        (x) =>
                          x.label +
                          (x.meterStatus === 'CONNECTED'
                            ? ''
                            : ` (${METER_STATUS_LABELS[x.meterStatus]})`),
                      )
                      .join('، ') || '—',
                },
                {
                  key: 'phone',
                  title: 'الهاتف',
                  width: 140,
                  render: (_: unknown, m: MemberRow) => (
                    <span className="ltr-num">{m.notifyPhone}</span>
                  ),
                },
              ],
              {
                key: 'contact',
                drop: 1,
                title: 'التواصل',
                width: 150,
                render: (_, m) =>
                  m.notifyChannel === 'NONE' ? (
                    <Tag>{CHANNEL_LABELS.NONE}</Tag>
                  ) : (
                    <Tag color={isReachable(m) ? 'green' : 'orange'}>
                      {PHONE_STATUS_LABELS[m.phoneStatus ?? 'MISSING']}
                    </Tag>
                  ),
              },
              {
                key: 'shart',
                drop: 2,
                title: 'الشرط',
                width: 110,
                render: (_, m) =>
                  m.unnamedShart.totalCount > 0 ? (
                    <Tag color="orange">بلا اسم</Tag>
                  ) : null,
              },
            ])}
          />
        </TableCard>
      </section>
      {split.panel === 'new' && (
        <MemberPanel
          key="new"
          id={null}
          onClose={split.close}
          onOpen={(id) => navigate(`/members/${id}`)}
          onDirtyChange={split.onDirtyChange}
        />
      )}
    </div>
  );
}
